import { AccountValueItem, Performance, ReturnsByYear } from 'types/performance';
import { constructUrlWithQueryParams, investmentEntityApsRequest, investmentEntityRequest } from '@utils/request';
import {
	IpoInvestmentPosition,
	IpoPositionDetail,
	NotePositionDetail,
	Positions,
	ReitPositionDetail
} from 'types/portfolio';
import { ModularNewsFeedResponse, NewsFeedByPageNumber, NewsFeedResponse } from 'types/account';
import { app } from '@store/modules/app';
import { getBlankReturnsByYear } from '@utils/account-performance-table-formatter';
import moment from 'moment';
import { RiaPlan } from 'types/plan';

export async function getAccountValueOverTimeData(): Promise<Array<AccountValueItem>> {
	const response = await investmentEntityApsRequest.get(`/performance/account-value-over-time`);
	return response.data;
}

export async function getPerformanceData(): Promise<Performance> {
	const response = await investmentEntityApsRequest.get(`/performance`);

	const tempPerformance: Performance = response.data;
	if (tempPerformance?.priorYearReturns?.notReportingNav) {
		const formattedData: ReturnsByYear[] = [];
		const blanks = getBlankReturnsByYear(tempPerformance.priorYearReturns?.allFunds?.returnsByYear ?? []);

		if (tempPerformance.priorYearReturns.notReportingNav.reits) {
			tempPerformance.priorYearReturns.notReportingNav.reits.forEach((reitReturnByYear) => {
				const sliceLength = blanks.length - reitReturnByYear.returnsByYear.length;

				reitReturnByYear.returnsByYear = formattedData.concat(
					blanks.slice(0, sliceLength),
					reitReturnByYear.returnsByYear
				);
			});
		}

		const sliceLength = blanks.length - tempPerformance.priorYearReturns.notReportingNav.returnsByYear.length;
		tempPerformance.priorYearReturns.notReportingNav.returnsByYear = formattedData.concat(
			blanks.slice(0, sliceLength),
			tempPerformance.priorYearReturns.notReportingNav.returnsByYear
		);
	}

	return tempPerformance;
}

export async function getPositionsTable(): Promise<Positions> {
	const response = await investmentEntityApsRequest.get(`/portfolio/positions-table`);
	return response.data;
}

export async function getNewsFeedByPageNumber(params: NewsFeedByPageNumber): Promise<ModularNewsFeedResponse> {
	const urlWithParams = constructUrlWithQueryParams('/news-feed/page-detailed-dividends', params);
	const response = await investmentEntityRequest.get(urlWithParams);
	const totalNumber = response.data?.meta?.totalNumber ?? '0';
	const offset = response.data?.meta?.nextPage?.offset ?? 0;
	const availableFilters = response.data.meta.availableFilters ?? [];

	return {
		items: response.data.data,
		totalNumber,
		offset: Number(offset),
		availableFilters: availableFilters
	};
}

export async function getNewsFeedFirstPage(filters: Record<string, unknown> = {}): Promise<NewsFeedResponse> {
	const urlWithParams = constructUrlWithQueryParams('/news-feed/page-detailed-dividends', filters);
	const response = await investmentEntityRequest.get(urlWithParams);
	const nextPage = response.data?.meta?.nextPage?.pageUrl ?? '';

	return {
		items: response.data.data,
		nextPage: nextPage.substring(nextPage.indexOf('/news-feed'))
	};
}

export async function getNewsFeedNextPage(nextPageUrl: string): Promise<NewsFeedResponse> {
	if (nextPageUrl) {
		const response = await investmentEntityRequest.get(nextPageUrl);
		const nextPage = response.data?.meta?.nextPage?.pageUrl ?? '';

		return {
			items: response.data.data,
			nextPage: nextPage.substring(nextPage.indexOf('/news-feed'))
		};
	} else {
		return {
			items: [],
			nextPage: ''
		};
	}
}

export async function getPublicNewsFeedByPageNumber(params: NewsFeedByPageNumber): Promise<ModularNewsFeedResponse> {
	const urlWithParams = constructUrlWithQueryParams('/news-feed/public', params);
	const response = await investmentEntityRequest.get(urlWithParams);
	const totalNumber = response.data?.meta?.totalNumber ?? '0';
	const offset = response.data?.meta?.nextPage?.offset ?? 0;
	const availableFilters = response.data.meta.availableFilters ?? [];

	return {
		items: response.data.data,
		totalNumber,
		offset: Number(offset),
		availableFilters: availableFilters
	};
}

export async function bookmarkInvestorUpdate(investorUpdateId: string): Promise<void> {
	const response = await investmentEntityRequest.post(`/investor-update/${investorUpdateId}/bookmark`);
	if (response.data.bookmarkCount === '1') {
		app.ADD_TOAST({
			type: 'success',
			message:
				'Update saved! You can access your saved updates via the filter dropdown on your Fundrise Newsfeed.'
		});
	}
}

export async function deleteBookmarkInvestorUpdate(investorUpdateId: string): Promise<void> {
	await investmentEntityRequest.delete(`/investor-update/${investorUpdateId}/bookmark`);
}

export async function getReitPositionDetail(reitId: string): Promise<ReitPositionDetail> {
	const response = await investmentEntityApsRequest.get(`/reit/${reitId}/position`);
	return response.data;
}

export async function getNotePositionDetail(noteId: string): Promise<NotePositionDetail> {
	const response = await investmentEntityApsRequest.get(`/note-investment/${noteId}/position`);

	response.data.issueDate = moment.parseZone(response.data.issueDate).format('MMM D, YYYY');
	response.data.maturityDate = moment.parseZone(response.data.maturityDate).format('MMM D, YYYY');
	return response.data;
}

export async function getIpoPositionDetail(): Promise<IpoPositionDetail> {
	const response = await investmentEntityRequest.get(`/ipo/position`);
	response.data.ipoInvestmentPositions.forEach((ipoInvestmentPosition: IpoInvestmentPosition) => {
		ipoInvestmentPosition.investmentDate = moment
			.parseZone(ipoInvestmentPosition.investmentDate)
			.format('MMM D, YYYY');
	});
	return response.data;
}

export async function getCurrentRiaPlan(): Promise<RiaPlan> {
	const response = await investmentEntityRequest.get(`/ria-plan`);
	return response.data;
}

export async function updateRiaPlan(planId: string): Promise<void> {
	await investmentEntityRequest.post(`/ria-plan/${planId}/update`);
}
